import Vue from 'vue';
import presentationApi from '../../api/presentationApi';
import offlineStore from '../../api/offlineStoreApi';
import userApi from '../../api/userApi';

const storeState = {
  all: {},
  attendedPresentations: [],
  currentPresentationId: undefined,
  currentPresentation: {},
  currentPresentationSlides: undefined,
  currentShowedSlide: undefined,
  viewedPresentationId: undefined,
  viewedPresentationFeedback: null,
  recentPresentations: [],
  recentPresentationIds: [],
  recentPresentationsLoading: true,
  showFeedback: false,
  presentationIdAfterSignIn: null
};

const storeGetters = {
  currentSlideImage: state => (state.currentPresentation.slides && typeof state.currentPresentation.currentSlide !== 'undefined'
    ? state.currentPresentation.slides[state.currentPresentation.currentSlide].image
    : undefined
  ),
  totalSlides: (state, getters) =>
    (getters.currentPresentation.slides ? getters.currentPresentation.slides.length : null),
  showedSlides: (state) => {
    let showedSlides;
    if (state.currentPresentation.presentationId && state.currentPresentationSlides) {
      showedSlides = state.currentPresentationSlides.slice(
        0, state.currentPresentation.currentSlide + 1
      ).reverse();
    }

    return showedSlides;
  },
  attendedPresentations: state => state.attendedPresentations,
  currentPresentation: (state, getters) => {
    const currentPresentation = state.all[state.currentPresentationId] || {};
    const currentAttended = getters.attendedPresentations.find(
      attended => attended.presentationId === state.currentPresentationId
    ) || {};

    return Object.assign(
      currentPresentation,
      currentAttended
    );
  },
  currentSlideIsFavorite: (state, getters) => {
    const currentPresentation = getters.currentPresentation;
    return currentPresentation && currentPresentation.favorites
      ? currentPresentation.favorites[getters.showedSlideIndex]
      : false;
  },
  slides: state => state.currentPresentationSlides,
  showedSlideIndex: (state) => {
    let showedSlideIndex;
    if (typeof state.currentShowedSlide !== 'undefined') {
      showedSlideIndex = state.currentShowedSlide;
    } else {
      showedSlideIndex = state.currentPresentation
        ? state.currentPresentation.currentSlide
        : undefined;
    }

    return showedSlideIndex;
  },
  currentPresentationEnded: state => state.currentPresentation.ended || false,
  feedback: (state, getters) => {
    const currentPresentation = getters.currentPresentation;
    return currentPresentation.feedback
      ? currentPresentation.feedback.find(feedback => feedback.userId === getters.userId)
      : undefined;
  },
  showFeedback: (state, getters) => state.showFeedback && !getters.feedback,
  recentPresentationsList: (state, getters) => {
    const presentationsList = [];
    state.recentPresentations.forEach((recentPresentation) => {
      const recentAttended = getters.attendedPresentations.find(
        attended => attended.presentationId === recentPresentation.presentationId
      ) || {};

      presentationsList.push(Object.assign(
        {},
        recentPresentation,
        recentAttended
      ));
    });

    return presentationsList;
  },
  recentPresentationsLoading: state => state.recentPresentationsLoading,
  viewedPresentation: (state, getters) => {
    const viewedPresentation = state.all[state.viewedPresentationId];
    const viewedAttended = getters.attendedPresentations.find(
      attended => attended.presentationId === state.viewedPresentationId
    );

    return Object.assign(
      {},
      viewedPresentation,
      viewedAttended
    );
  },
  viewedPresentationFeedback: state => state.viewedPresentationFeedback,
  favSlides: (state, getters) => (
    getters.viewedPresentation.slides && getters.viewedPresentation.favorites
      ? getters.viewedPresentation.slides.filter(
        (slide, index) => getters.viewedPresentation.favorites[index]
      )
      : undefined
  ),
  presentationIdAfterSignIn: state => state.presentationIdAfterSignIn
};

const actions = {
  viewPresentation({ commit }, presentationId) {
    commit('VIEW_PRESENTATION_ID', presentationId);
    presentationApi.fetchFeedback(presentationId).then((feedbackSnapshot) => {
      const feedback = [];
      feedbackSnapshot.forEach((doc) => {
        feedback.push(doc.data());
      });
      commit('VIEW_PRESENTATION_FEEDBACK', feedback);
    });
  },

  getCheckedInPresentation({ dispatch, commit }, presentationId) {
    commit('RESET_CURRENT_PRESENTATION');

    presentationApi.fetchPresentation(presentationId).then((presentationDoc) => {
      const presentationData = presentationDoc.data();

      commit('SET_VIEWED_SLIDE', undefined);
      commit('RECEIVE_CURRENT_PRESENTATION', { presentationId, presentationData });
      commit('UPDATE_RECENT_PRESENTATION', { presentationId, presentationData });

      dispatch('listenForPresentationChanges', presentationId);
      dispatch('updateUserAttendance', { presentationId, presentationData });

      offlineStore.setCurrentPresentation(presentationId, presentationData);
    });
  },

  listenForPresentationChanges({ dispatch, commit, getters }, presentationId) {
    presentationApi.listenForChanges(presentationId, (presentationData) => {
      commit('RECEIVE_CURRENT_PRESENTATION', { presentationId, presentationData });

      if (presentationData.ended && getters.currentPresentationEnded) {
        dispatch('endCurrentPresentation', { presentationId, presentationData });
        presentationApi.stopListenForChanges();
      }
    });
  },

  gotoSlide({ commit }, offset) {
    commit('SET_VIEWED_SLIDE', offset);
  },

  toggleFavoriteSlide({ commit, getters }, { toggle, presentationId }) {
    const showedSlideIndex = getters.showedSlideIndex;
    const attendedIndex = getters.attendedPresentations.findIndex(
      attendedPresentation => attendedPresentation.presentationId === presentationId
    );
    const attended = getters.attendedPresentations[attendedIndex];

    commit('UPDATE_FAVORITE_SLIDE', { showedSlideIndex, attendedIndex, toggle });

    userApi.updateFavoriteSlide(attended);

    presentationApi.updateFavoritedCounter(
      getters.currentPresentation.presentationId,
      getters.showedSlideIndex
    );
  },

  endCurrentPresentation({ commit }, { presentationId, presentationData }) {
    offlineStore.removeCurrentPresentation();
    commit('UPDATE_RECENT_PRESENTATION', { presentationId, presentationData });
    commit('END_PRESENTATION', presentationData);
  },

  endCurrentPresentedPresentation({ getters }) {
    presentationApi.endCurrentPresentatedPresentation(getters.currentDraft.presentationId);
  },

  userFetched({ commit }) {
    commit('RECENT_PRESENTATIONS_LOADED');
  },

  setAttendedPresentations({ commit }, recentPresentations) {
    commit('RECEIVE_RECENT_PRESENTATIONS', recentPresentations);
  },

  fetchRecentAttendedPresentations({ commit }, attendedPresentations) {
    commit('RECEIVE_ATTENDED', attendedPresentations);

    attendedPresentations.forEach((presentation, index) => {
      if (index < 3) {
        presentationApi.fetchPresentation(presentation.presentationId).then((presentationDoc) => {
          commit('UPDATE_RECENT_PRESENTATION', {
            presentationId: presentation.presentationId,
            presentationData: presentationDoc.data()
          });
        });
      }
    });
  },

  unsetRecentAttendedPresentations({ commit }) {
    offlineStore.setRecentPresentations([]);
    offlineStore.removeCurrentPresentation();
    commit('UNSET_RECENT_PRESENTATIONS');
  },

  getResumablePresentation({ commit }) {
    offlineStore.getCurrentPresentation().then((presentation) => {
      if (presentation) {
        const { presentationId, ...presentationData } = presentation;
        commit('RECEIVE_CURRENT_PRESENTATION', { presentationId, presentationData });
      }
    });
  },

  checkResumePresentation({ dispatch, getters }) {
    const currentPresentationId = getters.currentPresentation.presentationId;

    if (currentPresentationId) {
      presentationApi.fetchPresentation(currentPresentationId).then((presentationDoc) => {
        const presentationData = presentationDoc.data();

        if (presentationData.ended) {
          dispatch('endCurrentPresentation', {
            presentationId: currentPresentationId,
            presentationData
          });
        }
      });
    }
  },

  submitRating({ commit, getters }, { presentationId, rating, comment }) {
    const userId = getters.userId;
    presentationApi.updateFeedback({ presentationId, userId, rating, comment });
    commit('CLOSE_FEEDBACK_MODAL');
  },

  viewPresentationAfterSignIn({ commit }, presentationId) {
    commit('PRESENTATION_ID_AFTER_SIGNIN', presentationId);
  }
};

const mutations = {
  VIEW_PRESENTATION_ID(state, presentation) {
    state.viewedPresentationId = presentation;
  },
  VIEW_PRESENTATION_FEEDBACK(state, feedback) {
    state.viewedPresentationFeedback = feedback;
  },
  RESET_CURRENT_PRESENTATION(state) {
    state.currentPresentation = {};
    state.currentPresentationId = undefined;
    state.currentPresentationSlides = undefined;
  },
  RECEIVE_CURRENT_PRESENTATION(state, { presentationId, presentationData }) {
    Vue.set(state.currentPresentation, 'presentationId', presentationId);
    state.currentPresentation = Object.assign(
      {},
      state.currentPresentation,
      presentationData
    );

    if (!state.currentPresentationSlides) {
      state.currentPresentationSlides = presentationData.slides;
    }
    state.all[presentationId] = presentationData;
    state.currentPresentationId = presentationId;
  },
  RECEIVE_RECENT_PRESENTATIONS(state, recentPresentations) {
    state.recentPresentations = recentPresentations;

    recentPresentations.forEach((presentation) => {
      const { presentationId, ...presentationData } = presentation;
      state.all[presentationId] = presentationData;
    });
  },
  UPDATE_RECENT_PRESENTATION(state, { presentationId, presentationData }) {
    const recentPresentationsState = state.recentPresentations || [];
    const samePresentation = state.recentPresentations.find(
      presentation => presentation.presentationId === presentationId
    );
    const updatedRecentPresentation = Object.assign(
      { presentationId },
      presentationData
    );

    const recentPresentations = recentPresentationsState.filter(
      presentation => presentation.presentationId !== presentationId
    );

    if (samePresentation) {
      const mergedRecentPresentation = Object.assign(
        {},
        samePresentation,
        updatedRecentPresentation
      );
      recentPresentations.unshift(mergedRecentPresentation);
    } else {
      recentPresentations.unshift(updatedRecentPresentation);
    }

    if (state.recentPresentationIds.indexOf(presentationId) === -1) {
      state.recentPresentationIds.unshift(presentationId);
    }

    state.recentPresentations = recentPresentations.slice(0, 3);
    state.all[presentationId] = presentationData;
    offlineStore.setRecentPresentations(state.recentPresentations);
  },
  UNSET_RECENT_PRESENTATIONS(state) {
    state.recentPresentations = [];
    state.all = {};
  },
  RECENT_PRESENTATIONS_LOADED(state) {
    state.recentPresentationsLoading = false;
  },
  RECEIVE_ATTENDED(state, attendedPresentations) {
    state.attendedPresentations = attendedPresentations;
  },
  ADD_ATTENDED(state, attendedPresentation) {
    state.attendedPresentations.unshift(attendedPresentation);
  },
  UPDATE_FAVORITE_SLIDE(state, { showedSlideIndex, attendedIndex, toggle }) {
    Vue.set(state.attendedPresentations[attendedIndex].favorites, showedSlideIndex, toggle);
  },
  SET_VIEWED_SLIDE(state, offset) {
    state.currentShowedSlide = offset && state.currentPresentation.currentSlide
      ? state.currentPresentation.currentSlide - offset
      : undefined;
  },
  END_PRESENTATION(state) {
    state.showFeedback = true;
  },
  CLOSE_FEEDBACK_MODAL(state) {
    state.showFeedback = false;
  },
  PRESENTATION_ID_AFTER_SIGNIN(state, presentationId) {
    state.presentationIdAfterSignIn = presentationId;
  }
};

export default {
  state: storeState,
  getters: storeGetters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/presentations.js